import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';

const AREAS = ['conversations', 'kanban', 'tasks', 'agenda'];
const scalar = value => (typeof value === 'string' ? value : '');

// Accept a local destination in this account only. Never trust a return URL
// from a shared link, another account, or an external origin.
export const safeSettingsReturn = (value, accountId) => {
  if (
    typeof value !== 'string' ||
    value.length > 4096 ||
    !accountId ||
    !value.startsWith('/app/accounts/')
  )
    return '';
  if (/[\\\r\n]/.test(value)) return '';
  try {
    const url = new URL(value, 'https://settings.invalid');
    const prefix = `/app/accounts/${accountId}/`;
    if (
      url.origin !== 'https://settings.invalid' ||
      !url.pathname.startsWith(prefix)
    )
      return '';
    if (
      /\/settings(?:\/|$)/.test(url.pathname) ||
      url.searchParams.get('view') === 'configure'
    )
      return '';
    [
      'return_to',
      'config_area',
      'config_center',
      'config_scope',
      'task_settings',
    ].forEach(key => url.searchParams.delete(key));
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
};

export const settingsArea = route => {
  const explicit = scalar(route.query?.config_area);
  if (AREAS.includes(explicit)) return explicit;
  if (route.name === 'kanban_board')
    return route.query?.view === 'agenda' ? 'agenda' : 'kanban';
  if (route.name === 'tasks') return 'tasks';
  if (/\/(conversations|dashboard|inbox)(?:\/|$)/.test(route.path))
    return 'conversations';
  return '';
};

export const settingsReturnPath = route =>
  safeSettingsReturn(route.query?.return_to, route.params.accountId) ||
  safeSettingsReturn(route.fullPath, route.params.accountId);

export const settingsContextQuery = route => {
  const query = {};
  const returnTo = settingsReturnPath(route);
  if (returnTo) query.return_to = returnTo;
  const area = settingsArea(route);
  if (area) query.config_area = area;
  const funnelId = scalar(route.query?.funnel_id || route.query?.funnel);
  if (/^[1-9]\d*$/.test(funnelId)) query.funnel_id = funnelId;
  const fromWorld = scalar(route.query?.from_world);
  if (worldById(fromWorld)) query.from_world = fromWorld;
  return query;
};

export const settingsCenterTarget = (route, extraQuery = {}) => {
  const base = `/app/accounts/${route.params.accountId}/settings/menu`;
  const query = { ...settingsContextQuery(route), ...extraQuery };
  if (
    route.name === 'kanban_board' &&
    route.query?.view !== 'configure' &&
    query.return_to &&
    /^[1-9]\d*$/.test(String(query.funnel_id || ''))
  ) {
    const origin = new URL(query.return_to, 'https://settings.invalid');
    origin.searchParams.set('funnel_id', query.funnel_id);
    query.return_to = `${origin.pathname}${origin.search}${origin.hash}`;
  }
  return {
    path: isWorkspaceV2Path(route.path) ? workspaceV2Path(base) : base,
    query,
  };
};

export const settingsDestination = (router, route, item) => {
  const query = {
    ...settingsContextQuery(route),
    ...item.query,
    ...(item.funnelId ? { funnel_id: String(item.funnelId) } : {}),
    config_center: '1',
    config_scope: item.scope || 'account',
  };
  const resolved = router.resolve({
    name: item.routeName,
    params: { accountId: route.params.accountId },
    query,
  });
  return {
    path: isWorkspaceV2Path(route.path)
      ? workspaceV2Path(resolved.path)
      : resolved.path,
    query,
  };
};

// Nested editors keep the origin while they stay inside configuration screens.
export const inheritSettingsContext = (to, from) => {
  if (
    from.query?.config_center !== '1' ||
    to.query?.config_center !== undefined
  )
    return null;
  if (String(to.params.accountId) !== String(from.params.accountId))
    return null;
  if (!/\/(settings|profile)(?:\/|$)/.test(to.path)) return null;
  const context = settingsContextQuery(from);
  return {
    path: to.path,
    hash: to.hash,
    query: {
      ...context,
      ...to.query,
      config_center: '1',
      config_scope: /\/profile\//.test(to.path) ? 'personal' : 'account',
    },
  };
};
